import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, MailOpen, Mail, ExternalLink } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import { useT } from "@/i18n/LanguageContext";
import { Link } from "react-router-dom";

export default function AdminInquiries() {
    const t = useT();
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(null);
    const [filter, setFilter] = useState("all");
    const [confirmId, setConfirmId] = useState(null);
    const confirmTimer = useRef(null);
    useEffect(() => () => clearTimeout(confirmTimer.current), []);
    const { refreshUnread } = useAdmin();

    const load = () => api.get("/inquiries").then((r) => setItems(r.data)).catch(() => {});

    useEffect(() => { load(); }, []);

    const select = async (inq) => {
        setActive(inq);
        if (inq.product_id && !inq.product_slug) {
            try {
                const { data } = await api.get(`/products/${inq.product_id}`);
                setActive({ ...inq, product_slug: data.slug, product_category_slug: data.category_slug });
            } catch {}
        }
        if (!inq.read) {
            try {
                await api.patch(`/inquiries/${inq.id}/read`);
                setItems((prev) => prev.map((i) => (i.id === inq.id ? { ...i, read: true } : i)));
                refreshUnread();
            } catch {}
        }
    };

    const remove = async (inq) => {
        // First click arms the delete; a second click within 3s confirms it.
        if (confirmId !== inq.id) {
            setConfirmId(inq.id);
            clearTimeout(confirmTimer.current);
            confirmTimer.current = setTimeout(() => setConfirmId(null), 3000);
            return;
        }
        clearTimeout(confirmTimer.current);
        setConfirmId(null);
        try {
            await api.delete(`/inquiries/${inq.id}`);
            toast.success("Inquiry removed");
            if (active?.id === inq.id) setActive(null);
            load();
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || "Delete failed");
        }
    };

    const filtered = items.filter((i) => {
        if (filter === "unread") return !i.read;
        if (filter === "product") return i.kind === "product";
        if (filter === "showroom") return i.kind === "showroom";
        if (filter === "general") return i.kind === "general";
        return true;
    });

    const FILTERS = [
        { id: "all",      label: t("admin.inq.filter.all") },
        { id: "unread",   label: t("admin.inq.filter.unread") },
        { id: "product",  label: t("admin.inq.filter.product") },
        { id: "showroom", label: t("admin.inq.filter.showroom") },
        { id: "general",  label: t("admin.inq.filter.general") },
    ];

    const kindLabel = (kind) => {
        if (kind === "showroom") return t("admin.inq.kind.showroom");
        if (kind === "general")  return t("admin.inq.kind.general");
        return t("admin.inq.kind.product");
    };

    return (
        <div className="p-8 md:p-12" data-testid="admin-inquiries">
            <header className="mb-10">
                <p className="overline">{t("admin.inq.overline")}</p>
                <h1 className="font-serif text-4xl md:text-5xl mt-2">{t("admin.inq.title")}</h1>
            </header>

            <div className="flex flex-wrap gap-2 mb-6" data-testid="inquiry-filters">
                {FILTERS.map((f) => (
                    <button
                        key={f.id}
                        onClick={() => setFilter(f.id)}
                        data-testid={`inq-filter-${f.id}`}
                        className={`text-[0.7rem] uppercase tracking-[0.25em] px-4 py-2 border transition-colors ${
                            filter === f.id
                                ? "border-melilux-gold text-melilux-gold"
                                : "border-white/15 text-melilux-cream/65 hover:border-melilux-cream/40 hover:text-melilux-cream"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                {/* List */}
                <div
                    className="lg:col-span-5 border border-white/10 bg-melilux-bg max-h-[70vh] overflow-y-auto"
                    data-testid="inquiries-list"
                >
                    {filtered.length === 0 && (
                        <div className="text-center py-16 text-melilux-cream/50 text-sm">
                            {t("admin.inq.empty")}
                        </div>
                    )}
                    {filtered.map((i) => (
                        <button
                            key={i.id}
                            onClick={() => select(i)}
                            data-testid={`inquiry-${i.id}`}
                            className={`w-full text-left px-5 py-4 border-b border-white/5 hover:bg-white/[0.03] transition-colors ${
                                active?.id === i.id ? "bg-white/[0.04]" : ""
                            }`}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                    {i.read
                                        ? <MailOpen size={14} strokeWidth={1.5} className="text-melilux-cream/40 shrink-0" />
                                        : <Mail size={14} strokeWidth={1.5} className="text-melilux-gold shrink-0" />
                                    }
                                    <div className="min-w-0">
                                        <div className={`font-medium truncate ${i.read ? "text-melilux-cream/85" : "text-melilux-cream"}`}>
                                            {i.name}
                                        </div>
                                        <div className="text-xs uppercase tracking-[0.22em] text-melilux-cream/50 mt-0.5">
                                            {kindLabel(i.kind)}{i.product_name ? ` · ${i.product_name}` : ""}
                                        </div>
                                    </div>
                                </div>
                                <span className="text-xs uppercase tracking-[0.22em] text-melilux-cream/40 shrink-0">
                                    {formatDate(i.created_at)}
                                </span>
                            </div>
                            <p className="text-sm text-melilux-cream/55 mt-2 line-clamp-2">{i.message}</p>
                        </button>
                    ))}
                </div>

                {/* Detail */}
                <div
                    className="lg:col-span-7 border border-white/10 bg-melilux-bg p-8 min-h-[70vh]"
                    data-testid="inquiry-detail"
                >
                    {!active ? (
                        <div className="h-full flex items-center justify-center text-center text-melilux-cream/45">
                            <div>
                                <p className="overline">{t("admin.inq.select.overline")}</p>
                                <p className="font-serif text-2xl mt-3">{t("admin.inq.select.title")}</p>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div>
                                    <p className="overline">{kindLabel(active.kind)}</p>
                                    <h2 className="font-serif text-3xl mt-3">{active.name}</h2>
                                    <span className="inline-block mt-2 text-[0.65rem] uppercase tracking-[0.25em] border border-melilux-gold/40 text-melilux-gold px-2 py-1">
                                        {kindLabel(active.kind)}
                                    </span>
                                    <div className="flex flex-col gap-2 mt-3">
                                        <p className="text-base text-melilux-cream/50">
                                            {t("admin.inq.mail")}:{" "}
                                            <span className="text-melilux-cream font-medium">{active.email}</span>
                                        </p>
                                        {active.phone && (
                                            <p className="text-base text-melilux-cream/50">
                                                {t("admin.inq.phone")}:{" "}
                                                <span className="text-melilux-cream font-medium">{active.phone}</span>
                                            </p>
                                        )}
                                    </div>
                                    {active.product_name && (
                                        <Link
                                            to={`/collections/${active.product_category_slug}/${active.product_slug}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.25em] text-melilux-gold hover:text-melilux-goldBright transition-colors mt-3"
                                        >
                                            Re: {active.product_name}
                                            <ExternalLink size={12} strokeWidth={1.5} />
                                        </Link>
                                    )}
                                </div>
                                <button
                                    onClick={() => remove(active)}
                                    data-testid="inquiry-delete"
                                    className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] transition-colors ${confirmId === active.id ? "text-destructive" : "text-melilux-cream/55 hover:text-destructive"}`}
                                >
                                    <Trash2 size={14} strokeWidth={1.5} /> {confirmId === active.id ? t("admin.delete.again") : t("admin.inq.delete")}
                                </button>
                            </div>

                            <div className="border-t border-white/10 mt-8 pt-8">
                                <p className="overline">{t("admin.inq.message")}</p>
                                <p className="text-melilux-cream/85 leading-relaxed mt-4 whitespace-pre-wrap">
                                    {active.message}
                                </p>
                            </div>

                            <div className="mt-10 flex flex-wrap gap-3">
                                <button
                                    onClick={() => window.open(`mailto:${active.email}?subject=Re: Your Melilux inquiry`)}
                                    className="bg-melilux-gold text-melilux-bg px-6 py-3 text-[0.7rem] uppercase tracking-[0.28em] hover:opacity-80 transition-opacity"
                                    data-testid="inquiry-reply"
                                >
                                    {t("admin.inq.reply")}
                                </button>
                            </div>

                            <div className="text-xs uppercase tracking-[0.22em] text-melilux-cream/40 mt-10">
                                {t("admin.inq.received")} {formatDate(active.created_at)}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function formatDate(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
        return "";
    }
}